// GROUND-M M2 DRAWN SET + GEOMORPH (pure; the client's groundtree.js and the
// gate scripts/check-groundm.mjs run the SAME code). APP_REARCH §2h.
//
// drawSet: the nodes drawn for one eye, frame to frame (state `st`):
//   - a node splits where its error on screen exceeds px: its children are
//     wanted while the eye's 3D distance to the child's box d < D = err * K /
//     px of the parent; once split it merges back only past merge * D
//     (hysteresis). No 2:1 balance: a stitched mesh needs none.
//   - quads: a split node keeps drawing the quadrants its own error still
//     serves ('k_tx_ty#q', q = dy * 2 + dx), refining only the others.
//   - R_STAND (stand, m): only L0 is drawn there. A level-k node splits where
//     a child's plan distance < Rs(k) = stand + standW * k (the cascade: a
//     parent is fully morphed before its children start), and the child's
//     stand morph runs 0 -> 1 over [Rs(k), Rs(k) - 0.9 standW] — m = 1 on
//     every L0 inside stand, and on every node the split's 0.1 standW
//     hysteresis holds split (else its release dropped a whole subtree onto
//     a parent at m < 1: a pop).
//   - a child is drawn only when the parent is fully morphed (m = 1) and all
//     wanted children are resident (ready); a wanted child not ready is a
//     MISS (counted; the parent's quadrant holds), inside R_STAND a stand miss.
//   - wedge (t40, the optic): a child whose plan disc meets the wedge (apex
//     ox / oz, azimuth ax / az, half-angle half: focus.js hit with no range)
//     splits at the wedge's K, every other one at K — the optic's true
//     pixel angle only where it looks, the eye's everywhere else.
//   - fade (no pop on a K drop: the wedge turning away, a zoom-out): a child
//     drawn last frame at m > 0 (or still split) that is no longer wanted
//     stays drawn, its m falling to 0 over tMorph (a split one holds its m
//     until its own children merged: deepest first), then merges — at m = 0
//     it draws its parent's surface. Every walked child's m moves at most
//     dt / tMorph a frame (inside R_STAND exempt: m = 1 there at once). A
//     distance merge happens at m = 0 already: unchanged. morph false: off.
// geomorph (owner: no pop, replaces V_ENV): per drawn node
//   m = clamp((D - d) / (band * D)) on the child's box (3D), the parent's D;
//   m = max(m, m_stand); late arrival m = min(m, m_time), m_time = (now -
//   first drawn) / tMorph; m_stand >= 1 -> 1 (inside R_STAND never < 1).
// A vertex draws y = aH - (1 - slot[tag]) * delta: delta = its height minus
// the parent's surface there (the decode worker's from-heights), tag 0 the
// node's m, 1..4 its sides (top j=0, bottom j=N, left i=0, right i=N), 5..8
// its corners (i0j0, iNj0, i0jN, iNjN). slotsOf gives every drawn node its
// nine values such that any vertex two nodes share evaluates identically
// (stitch.js makes the shared vertices the coarse node's own).
import { nodeSide, offFrame } from './lookup.js';

export const LOD_M = { band: 0.3, merge: 1.1, stand: 64, standW: 16, tMorph: 0.5 };
const clamp01 = (v) => (v <= 0 ? 0 : v >= 1 ? 1 : v);

// index = { nodes: Map key -> { k, tx, ty, err, y0, y1 }, world: { w, h,
// outer } }: each node gets Y0 / Y1 = its own range joined with every
// descendant's (so a child's box always lies in its parent's: d(child) >=
// d(parent)), and kids (the four children present, or null: none / some
// missing inside the frame [0, 0, w, h]); key ('k_tx_ty'), qkey (its four
// quadrant entries' 'k_tx_ty#q') and up (the parent, or null) made once here,
// never a template string in the frame.
// A child missing OUTSIDE the frame (any side; tx / ty may be negative) is a
// null kid:
//   - world.outer (meta.groundm.outer: the far tree, rearch-r5d far flip): off
//     the TREE — the parent draws that quadrant itself (n.hold bit q; the
//     depth rule gives a far chunk a coarse first level, so a node out there
//     may have only some children). A node with no child at all is a leaf.
//   - else (frame only): off the WORLD — nothing is drawn there.
// A child missing INSIDE the frame stays a hole: the node never splits.
export function prepIndex(index) {
  const levels = [...new Set([...index.nodes.values()].map((n) => n.k))].sort((a, b) => a - b);
  for (const n of index.nodes.values()) {
    n.key = `${n.k}_${n.tx}_${n.ty}`;
    n.qkey = [0, 1, 2, 3].map((q) => `${n.key}#${q}`);
    n.up = index.nodes.get(`${n.k + 1}_${n.tx >> 1}_${n.ty >> 1}`) || null;
  }
  index.tops = new Map();
  const W = index.world, outer = !!(W && W.outer);
  for (const k of levels) {
    for (const n of index.nodes.values()) {
      if (n.k !== k) continue;
      n.Y0 = n.y0; n.Y1 = n.y1; n.kids = null; n.hold = 0;
      if (k === 0) continue;
      const r = [];
      let ok = true, any = false, hold = 0;
      for (let dy = 0; dy < 2 && ok; dy++) for (let dx = 0; dx < 2; dx++) {
        const cx = 2 * n.tx + dx, cy = 2 * n.ty + dy, cs = nodeSide(k - 1);
        const c = index.nodes.get(`${k - 1}_${cx}_${cy}`);
        if (!c) {
          if (W && offFrame(cx, cy, cs, W)) { r.push(null); if (outer) hold |= 1 << (dy * 2 + dx); continue; }
          ok = false; break;
        }
        r.push(c); any = true;
        if (c.Y0 < n.Y0) n.Y0 = c.Y0;
        if (c.Y1 > n.Y1) n.Y1 = c.Y1;
      }
      // [q0, q1, q2, q3], null where off the frame; hold: the quadrants the
      // node draws itself (the far tree)
      if (ok && (any || !outer)) { n.kids = r; n.hold = hold; }
    }
  }
  index.top = levels[levels.length - 1];
  return index;
}


// the level-top nodes in index order (drawSet's roots), once per top
function topsOf(index, top) {
  let t = index.tops.get(top);
  if (!t) index.tops.set(top, (t = [...index.nodes.values()].filter((n) => n.k === top)));
  return t;
}

export function newState() { return { split: new Set(), since: new Map(), m: new Map(), at: null, frame: 0 }; }

// does node c's plan disc meet the wedge g (focus.js hit, no range)
export function inWedge(g, c) {
  const s = nodeSide(c.k), h = s * Math.SQRT1_2;
  const dx = c.tx * s + 0.25 + 0.5 * s - g.ox, dz = c.ty * s + 0.25 + 0.5 * s - g.oz, d = Math.hypot(dx, dz);
  if (d <= h) return true;
  return Math.acos(Math.max(-1, Math.min(1, (dx * g.ax + dz * g.az) / d))) <= g.half + Math.asin(Math.min(1, h / d));
}

// opt: { px, K, stand, standW, band, merge, tMorph, quads, top, morph (false:
// m = 1 always, no fade — the pop twin), qok(key) -> bool (no leaf spans
// quadrants; default yes), wedge ({ ox, oz, ax, az, half, K } or null: the
// optic's) }; ready(key) -> bool (resident, decoded). -> { draw: Map pkey
// -> { n, quad, m, fk, fx, fy } (fk / fx / fy: the footprint), miss: keys
// wanted but not ready, standMiss, standPop, fading, settled } — settled: a
// call at the same eye / opt / readiness returns the same, whatever its now
// (every walked m equal to last frame's and free of the clock: no fade, no
// m held by m_time or moved by the rate limit, the same split set; stillDraw)
// THE APRONS (the far tree, groundm/index.json `aprons`: one L4 GM03 per
// frame chunk straddling a non-chunk-aligned frame edge, drawn under the
// frame's edge band and past it): the aprons [{ tx, ty, k }] a drawn set
// (drawSet's draw: entries { n, fk, fx, fy }) shows — an apron draws while an
// entry of a FRAME-BUILT node (k <= first, meeting the frame `world` {w, h})
// overlaps its square (strictly: a neighbour sharing an edge does not), never
// for outer nodes alone. -> Set 'tx_ty'
export function apronsDrawn(aprons, draw, world, first = 4) {
  const on = new Set();
  if (!aprons.length) return on;
  const fb = [];
  for (const e of draw.values()) {
    const n = e.n;
    if (n.k > first || offFrame(n.tx, n.ty, nodeSide(n.k), world)) continue;
    const s = nodeSide(e.fk);
    fb.push([e.fx * s, e.fy * s, (e.fx + 1) * s, (e.fy + 1) * s]);
  }
  for (const a of aprons) {
    const s = nodeSide(a.k ?? 4), x0 = a.tx * s, z0 = a.ty * s, x1 = x0 + s, z1 = z0 + s;
    if (fb.some((b) => b[0] < x1 && b[2] > x0 && b[1] < z1 && b[3] > z0)) on.add(`${a.tx}_${a.ty}`);
  }
  return on;
}

export function drawSet(index, eye, opt, st, now = 0, ready = () => true) {
  const o = { ...LOD_M, ...opt };
  const K = o.K, px = o.px, R = o.stand || 0, W = o.standW;
  const top = o.top ?? index.top;
  const G = o.wedge && o.wedge.K > K ? o.wedge : null;
  const Kc = (c) => (G && inWedge(G, c) ? G.K : K);
  // the fade: last frame's m per walked node, the most m may move this frame
  const fadeOn = o.morph !== false && !!st.m && st.at !== null && st.frame > 0;
  const prevM = fadeOn ? st.m : null, mNow = new Map();
  const dF = !fadeOn ? Infinity : o.tMorph > 0 ? Math.max(0, now - st.at) / (o.tMorph * 1000) : Infinity;
  let fading = 0, settled = fadeOn;
  const prevSplit = st.split;
  const box = (n, Y0, Y1) => {
    const s = nodeSide(n.k), x0 = n.tx * s + 0.25, z0 = n.ty * s + 0.25;
    const dx = Math.max(x0 - eye.x, 0, eye.x - (x0 + s)), dz = Math.max(z0 - eye.z, 0, eye.z - (z0 + s));
    const dy = Math.max(Y0 - eye.y, 0, eye.y - Y1);
    return Math.hypot(dx, dy, dz);
  };
  const plan = (n) => {
    const s = nodeSide(n.k), x0 = n.tx * s + 0.25, z0 = n.ty * s + 0.25;
    return Math.hypot(Math.max(x0 - eye.x, 0, eye.x - (x0 + s)), Math.max(z0 - eye.z, 0, eye.z - (z0 + s)));
  };
  const Rs = (k) => R + W * k;
  const draw = new Map(), miss = [], split = new Set();
  let standMiss = 0, standPop = 0;
  const first = st.frame === 0;
  // a node's first frame in the walk (this run of frames: one it leaves
  // the walk forgets — a later return morphs in again)
  const seen = new Set();
  const mTime = (key) => {
    seen.add(key);
    let t = st.since.get(key);
    if (t === undefined) { t = first ? -Infinity : now; st.since.set(key, t); }
    return o.tMorph > 0 ? clamp01((now - t) / (o.tMorph * 1000)) : 1;
  };
  const put = (n, quad, m) => {
    if (quad === undefined) draw.set(n.key, { n, quad, m, fk: n.k, fx: n.tx, fy: n.ty });
    else draw.set(n.qkey[quad], { n, quad, m, fk: n.k - 1, fx: 2 * n.tx + (quad & 1), fy: 2 * n.ty + (quad >> 1) });
  };
  // the child's m under parent n (D the parent's)
  const childM = (n, c, D) => {
    if (o.morph === false) return 1;
    // stamped first, also under a forced stand: the node leaving R_STAND
    // keeps the time it has been drawn (else m fell 1 -> 0 there: a pop)
    const ck = c.key, mt = mTime(ck);
    const md = D > 0 ? clamp01((D - box(c, c.Y0, c.Y1)) / (o.band * D)) : 1;
    const ms = R > 0 ? clamp01((Rs(n.k) - plan(c)) / (0.9 * W)) : 0;
    const pm = prevM && prevM.get(ck);
    if (ms >= 1) { if (pm !== 1) settled = false; return 1; }
    const x = Math.max(md, ms);
    let m = Math.min(x, mt);
    // a K step (the wedge's edge, a zoom) moves md at once: the rate limit;
    // a node split last frame holds its m (its children draw on its full
    // surface: they merge first, deepest first)
    if (pm !== undefined) {
      const held = st.split.has(ck);
      m = Math.min(pm + dF, Math.max(held ? pm : pm - dF, m));
      // settled: m_time past it, m = last frame's for any dF (at it, or held over it)
      if (!(mt >= x && m === pm && (x === pm || (held && x <= pm)))) settled = false;
    } else settled = false;
    return m;
  };
  // child c, no longer wanted, drawn last frame: its fading m, or -1 (merge)
  const fadeM = (c) => {
    if (!prevM) return -1;
    const ck = c.key, pm = prevM.get(ck);
    if (pm === undefined || !ready(ck)) return -1;
    // merged only once it drew m = 0 (the frame it reaches 0 draws it)
    const held = st.split.has(ck), fm = held ? pm : Math.max(0, pm - dF);
    if (!held && pm <= 0) return -1;
    // a re-want rises from here, not from its old first-drawn time
    seen.add(ck);
    st.since.set(ck, o.tMorph > 0 ? now - fm * o.tMorph * 1000 : -Infinity);
    return fm;
  };
  // per level scratch (a walk's children are one level down: never shared
  // along the recursion)
  const scr = [];
  for (let k = 0; k <= top; k++) scr.push({ D: new Float64Array(4), wantE: new Uint8Array(4), wantS: new Uint8Array(4), refine: new Uint8Array(4), fm: new Float64Array(4) });
  const walk = (n, m) => {
    const key = n.key;
    if (settled && prevM.get(key) !== m) settled = false;
    mNow.set(key, m);
    const kids = n.k > 0 && n.k <= top ? n.kids : null;
    if (!kids) { put(n, undefined, m); return; }
    const was = st.split.has(key);
    // per quadrant: D (the wedge's K where the child meets it), wanted by
    // error, by R_STAND
    const { D, wantE, wantS, refine, fm } = scr[n.k];
    let anyE = false, anyS = false;
    for (let q = 0; q < 4; q++) {
      const c = kids[q];
      if (!c) { D[q] = 0; wantE[q] = wantS[q] = 0; continue; }
      const cb = o.quads ? c : n, d = box(cb, cb.Y0 ?? n.Y0, cb.Y1 ?? n.Y1), p = plan(cb);
      D[q] = n.err * Kc(cb) / px;
      if ((wantE[q] = d < D[q] * (was ? o.merge : 1) ? 1 : 0)) anyE = true;
      if ((wantS[q] = R > 0 && p < Rs(n.k) + (was ? 0.1 * W : 0) ? 1 : 0)) anyS = true;
    }
    const qok = o.quads && (!o.qok || o.qok(key));
    // a held quadrant (the far tree: no child there) draws only as the
    // node's own quadrant entry: all or nothing keeps the node whole
    if (!qok && n.hold) { put(n, undefined, m); return; }
    if (!qok) {
      // all or nothing
      const any = anyE || anyS, forced = anyS;
      const live = kids.filter(Boolean);
      const allReady = live.every((c) => ready(c.key));
      if (!any || !allReady || (m < 1 && !forced)) {
        if (any && !allReady) { for (const c of live) if (!ready(c.key)) miss.push(c.key); if (forced) standMiss++; }
        // all drawn last frame: they fade together (one done draws at 0)
        const fm = was && allReady ? live.map(fadeM) : [];
        if (fm.some((v) => v >= 0)) {
          split.add(key); fading += live.length;
          live.forEach((c, i) => walk(c, Math.max(0, fm[i])));
          return;
        }
        put(n, undefined, m); return;
      }
      if (m < 1) standPop++;
      split.add(key);
      for (let q = 0; q < 4; q++) if (kids[q]) walk(kids[q], childM(n, kids[q], D[q]));
      return;
    }
    refine.fill(0); fm.fill(-1);
    let nRef = 0;
    for (let q = 0; q < 4; q++) {
      const c = kids[q];
      if (!c) continue;
      const ck = c.key;
      if (wantE[q] || wantS[q]) {
        if (!ready(ck)) { miss.push(ck); if (wantS[q]) standMiss++; continue; }
        if (!(m < 1 && !wantS[q])) {        // else: the parent morphs in first
          if (m < 1) standPop++;
          refine[q] = 1; nRef++;
          continue;
        }
      }
      // not refined now, drawn last frame: it fades out first
      if (was && (fm[q] = fadeM(c)) >= 0) { refine[q] = 1; nRef++; fading++; }
    }
    if (!nRef) { put(n, undefined, m); return; }
    split.add(key);
    for (let q = 0; q < 4; q++) {
      const c = kids[q];
      if (!c) { if ((n.hold >> q) & 1) put(n, q, m); continue; }
      if (refine[q]) walk(c, fm[q] >= 0 ? fm[q] : childM(n, c, D[q]));
      else put(n, q, m);
    }
  };
  for (const n of topsOf(index, top)) walk(n, 1);
  for (const key of st.since.keys()) if (!seen.has(key)) st.since.delete(key);
  if (settled && (fading || split.size !== prevSplit.size)) settled = false;
  if (settled) for (const key of split) if (!prevSplit.has(key)) { settled = false; break; }
  st.split = split;
  st.m = mNow;
  st.at = now;
  st.frame++;
  return { draw, miss, standMiss, standPop, fading, settled };
}

// ------------------------------------------------------------ the still cache
// a selection's view: the eye and every opt field drawSet reads (opt.qok and
// ready() are the caller's residency: its gen)
const SEL_KEYS = ['K', 'px', 'stand', 'standW', 'band', 'merge', 'tMorph', 'quads', 'top', 'morph'];
const WEDGE_KEYS = ['ox', 'oz', 'ax', 'az', 'half', 'K'];
export function selView(eye, opt) {
  const v = [eye.x, eye.y, eye.z];
  for (const k of SEL_KEYS) v.push(opt[k]);
  const g = opt.wedge;
  if (g) for (const k of WEDGE_KEYS) v.push(g[k]); else v.push(null);
  return v;
}
export function sameView(v, eye, opt) {
  if (!v || v[0] !== eye.x || v[1] !== eye.y || v[2] !== eye.z) return false;
  let i = 3;
  for (const k of SEL_KEYS) if (v[i++] !== opt[k]) return false;
  const g = opt.wedge;
  if (!g) return v[i] === null;
  if (v[i] === null) return false;
  for (const k of WEDGE_KEYS) if (v[i++] !== g[k]) return false;
  return true;
}
export function newStill() { return { r: null, v: null, gen: null, st: null, hits: 0 }; }
// THE STILL CACHE: drawSet, unless the last call (on this st) was settled at
// this very view (exact: eye xyz, every opt field, the wedge's) and residency
// generation gen (the caller's count of every change ready() / opt.qok read:
// a node decoded or evicted, a pool tile in or out) — then its result again,
// the clock moved as drawSet moves it (st.at, st.frame; the split / m / since
// it would write are the ones held). Exact keys only: VR head jitter misses,
// never a threshold.
export function stillDraw(C, index, eye, opt, st, now, ready, gen) {
  if (C.r && C.r.settled && C.st === st && C.gen === gen && sameView(C.v, eye, opt)) {
    st.at = now; st.frame++; C.hits++;
    return C.r;
  }
  const r = drawSet(index, eye, opt, st, now, ready);
  C.r = r; C.st = st; C.gen = gen; C.v = selView(eye, opt);
  return r;
}

// ------------------------------------------------------------ slots
// the drawn entries by footprint 'fk_fx_fy'
export function footprints(draw) {
  const fp = new Map();
  for (const [pk, e] of draw) fp.set(`${e.fk}_${e.fx}_${e.fy}`, e);
  return fp;
}
// the drawn entry whose footprint covers the level-fk cell (cx, cy), at
// footprint level >= fk (null: finer entries there, or nothing)
export function coverOf(fp, fk, cx, cy, top) {
  for (let L = fk; L <= top; L++) {
    const e = fp.get(`${L}_${cx >> (L - fk)}_${cy >> (L - fk)}`);
    if (e) return e;
  }
  return null;
}
// side s of a footprint: the neighbouring cell's offset
export const SIDE_D = [[0, -1], [0, 1], [-1, 0], [1, 0]];     // top, bottom, left, right

// The slot a drawn entry e draws at world point (wx, wz) on its footprint's
// boundary (L0 sample units: u = (world - 0.25) / 0.5) reads, of its node's
// nine. Its node's corner -> the corner slot; its node's side -> the side
// slot; else (a quadrant's inner line) the node slot.
function slotAt(e, u, v) {
  const n = e.n, S = 256 << n.k, x0 = n.tx * S, z0 = n.ty * S;
  const onL = u === x0, onR = u === x0 + S, onT = v === z0, onB = v === z0 + S;
  if ((onL || onR) && (onT || onB)) return 5 + (onR ? 1 : 0) + (onB ? 2 : 0);
  if (onT) return 1;
  if (onB) return 2;
  if (onL) return 3;
  if (onR) return 4;
  return 0;
}
const valueAt = (e, slots, u, v) => slots[slotAt(e, u, v)];

// -> Map node key ('k_tx_ty') -> Float32Array(9) slot values, and per entry
// its sides' categories. Processed coarse mesh first: a value a node adopts
// from a coarser one is that one's final value.
export function slotsOf(draw, top) {
  const fp = footprints(draw);
  const byNode = new Map();       // node key -> entries
  for (const e of draw.values()) {
    const key = `${e.n.k}_${e.n.tx}_${e.n.ty}`;
    (byNode.get(key) || byNode.set(key, []).get(key)).push(e);
  }
  const keys = [...byNode.keys()].sort((a, b) => byNode.get(b)[0].n.k - byNode.get(a)[0].n.k);
  const slots = new Map();
  for (const key of keys) {
    const es = byNode.get(key), n = es[0].n, m = es[0].m, S = 256 << n.k;
    const sl = new Float32Array(9).fill(m);
    const coarseSide = [null, null, null, null];
    // sides: the node's side s, over the entries lying on it
    for (let s = 0; s < 4; s++) {
      let val = m, adopted = false;
      for (const e of es) {
        // does this entry's footprint lie on the node's side s?
        const fS = 256 << e.fk, fx0 = e.fx * fS, fz0 = e.fy * fS, x0 = n.tx * S, z0 = n.ty * S;
        const on = s === 0 ? fz0 === z0 : s === 1 ? fz0 + fS === z0 + S : s === 2 ? fx0 === x0 : fx0 + fS === x0 + S;
        if (!on) continue;
        const [dx, dy] = SIDE_D[s];
        const nb = coverOf(fp, e.fk, e.fx + dx, e.fy + dy, top);
        if (!nb) {
          // finer footprints there: a same-level node's quadrants (the same
          // mesh level, the same border) share the side's slot
          if (e.fk === 0) continue;
          for (let h = 0; h < 2; h++) {
            const cx = 2 * (e.fx + dx) + (dx ? (dx < 0 ? 1 : 0) : h), cy = 2 * (e.fy + dy) + (dy ? (dy < 0 ? 1 : 0) : h);
            const f = fp.get(`${e.fk - 1}_${cx}_${cy}`);
            if (f && f.n.k === n.k && f.n !== n && !adopted) val = Math.min(val, f.m);
          }
          continue;
        }
        if (nb.n === n) continue;
        if (nb.n.k > n.k) {
          // a coarser mesh: its value along the shared line
          const ns = slots.get(`${nb.n.k}_${nb.n.tx}_${nb.n.ty}`);
          const lu = s === 2 ? x0 : s === 3 ? x0 + S : fx0 + 0.5 * fS, lv = s === 0 ? z0 : s === 1 ? z0 + S : fz0 + 0.5 * fS;
          val = valueAt(nb, ns, lu, lv); adopted = true;
          coarseSide[s] = nb;
        } else if (nb.n.k === n.k && !adopted) val = Math.min(val, nb.m);
      }
      sl[1 + s] = val;
    }
    // corners: the coarsest mesh touching the corner point
    for (let c = 0; c < 4; c++) {
      const u = n.tx * S + (c & 1 ? S : 0), v = n.ty * S + (c & 2 ? S : 0);
      // the drawn entries whose closed footprint holds (u, v): the four cells
      // around it at the node's own level and up
      let best = null, mn = m;
      for (let L = 0; L <= top; L++) {
        const cs = 256 << L;
        for (const [ox, oy] of [[0, 0], [-1, 0], [0, -1], [-1, -1]]) {
          const cx = Math.floor(u / cs) + ox, cy = Math.floor(v / cs) + oy;
          const e = fp.get(`${L}_${cx}_${cy}`);
          if (!e) continue;
          const fS = cs, fx0 = cx * cs, fz0 = cy * cs;
          if (u < fx0 || u > fx0 + fS || v < fz0 || v > fz0 + fS) continue;
          if (e.n.k > n.k && (!best || e.n.k > best.n.k)) best = e;
          else if (e.n.k === n.k) mn = Math.min(mn, e.m);
        }
      }
      sl[5 + c] = best ? valueAt(best, slots.get(`${best.n.k}_${best.n.tx}_${best.n.ty}`), u, v) : mn;
    }
    slots.set(key, sl);
  }
  return slots;
}

// the drawn height of one vertex (float32, the vertex shader's arithmetic)
const f32 = Math.fround;
export function yOf(aH, d, slot) { return f32(f32(aH) - f32(f32(1 - f32(slot)) * f32(d))); }

// ------------------------------------------------------------ the slot plan
// slotsOf's every value is, by the drawn set's STRUCTURE alone (its keys: one
// plan per sig), either the min over some entries' m (the node's own first)
// or one slot of a coarser node (adopted): slotPlan records which (slotsOf's
// walk with the values left symbolic), slotsRun evaluates it on a set of that
// sig — per frame only the m move. Bit-identical: min is exact and
// order-free, the f32 store the same, an adopted slot read from the coarser
// node's f32 slot as slotsOf reads it (coarse first). An entry is referenced
// by its pkey (a shown set mixes this frame's entries with older ones: per
// entry m); a node's own m is its FIRST entry's in the set's order — every
// drawn set is in drawSet's walk order (one fixed order of pkeys), so one
// plan holds for every set of its sig.
// -> { n, ent: [pkey], M, code, all, slots: Map node key -> its nine (views of all) }
const CORNER_D = [[0, 0], [-1, 0], [0, -1], [-1, -1]];
export function slotPlan(draw, top) {
  const fp = footprints(draw);
  const ent = [], eOf = new Map();
  for (const [pk, e] of draw) { eOf.set(e, ent.length); ent.push(pk); }
  const keyOf = (n) => n.key ?? `${n.k}_${n.tx}_${n.ty}`;
  const byNode = new Map();
  for (const e of draw.values()) {
    const key = keyOf(e.n);
    (byNode.get(key) || byNode.set(key, []).get(key)).push(e);
  }
  const keys = [...byNode.keys()].sort((a, b) => byNode.get(b)[0].n.k - byNode.get(a)[0].n.k);
  const at = new Map(keys.map((key, i) => [key, i]));
  // per node: its own entry, then slots 1..8 each [-1 - flat index] (a
  // coarser node's slot) or [count, entry...] (their min)
  const code = [];
  const emit = (cp, mins) => { if (cp) code.push(-1 - (9 * at.get(keyOf(cp.e.n)) + slotAt(cp.e, cp.u, cp.v))); else code.push(mins.length, ...mins); };
  for (const key of keys) {
    const es = byNode.get(key), n = es[0].n, own = eOf.get(es[0]), S = 256 << n.k;
    code.push(own);
    // sides (slotsOf's loop)
    for (let s = 0; s < 4; s++) {
      let mins = [own], cp = null, adopted = false;
      for (const e of es) {
        const fS = 256 << e.fk, fx0 = e.fx * fS, fz0 = e.fy * fS, x0 = n.tx * S, z0 = n.ty * S;
        const on = s === 0 ? fz0 === z0 : s === 1 ? fz0 + fS === z0 + S : s === 2 ? fx0 === x0 : fx0 + fS === x0 + S;
        if (!on) continue;
        const [dx, dy] = SIDE_D[s];
        const nb = coverOf(fp, e.fk, e.fx + dx, e.fy + dy, top);
        if (!nb) {
          if (e.fk === 0) continue;
          for (let h = 0; h < 2; h++) {
            const cx = 2 * (e.fx + dx) + (dx ? (dx < 0 ? 1 : 0) : h), cy = 2 * (e.fy + dy) + (dy ? (dy < 0 ? 1 : 0) : h);
            const f = fp.get(`${e.fk - 1}_${cx}_${cy}`);
            if (f && f.n.k === n.k && f.n !== n && !adopted) mins.push(eOf.get(f));
          }
          continue;
        }
        if (nb.n === n) continue;
        if (nb.n.k > n.k) {
          const lu = s === 2 ? x0 : s === 3 ? x0 + S : fx0 + 0.5 * fS, lv = s === 0 ? z0 : s === 1 ? z0 + S : fz0 + 0.5 * fS;
          cp = { e: nb, u: lu, v: lv }; adopted = true;
        } else if (nb.n.k === n.k && !adopted) mins.push(eOf.get(nb));
      }
      emit(cp, mins);
    }
    // corners (slotsOf's loop)
    for (let c = 0; c < 4; c++) {
      const u = n.tx * S + (c & 1 ? S : 0), v = n.ty * S + (c & 2 ? S : 0);
      let best = null;
      const mins = [own];
      for (let L = 0; L <= top; L++) {
        const cs = 256 << L;
        for (const [ox, oy] of CORNER_D) {
          const cx = Math.floor(u / cs) + ox, cy = Math.floor(v / cs) + oy;
          const e = fp.get(`${L}_${cx}_${cy}`);
          if (!e) continue;
          const fx0 = cx * cs, fz0 = cy * cs;
          if (u < fx0 || u > fx0 + cs || v < fz0 || v > fz0 + cs) continue;
          if (e.n.k > n.k && (!best || e.n.k > best.n.k)) best = e;
          else if (e.n.k === n.k) mins.push(eOf.get(e));
        }
      }
      emit(best && { e: best, u, v }, mins);
    }
  }
  const all = new Float32Array(9 * keys.length);
  return { n: keys.length, ent, M: new Float64Array(ent.length), code: Int32Array.from(code), all,
    slots: new Map(keys.map((key, i) => [key, all.subarray(9 * i, 9 * i + 9)])) };
}

// the plan on a set of its sig -> plan.slots (overwritten by the next run)
export function slotsRun(P, draw) {
  const M = P.M, A = P.all, C = P.code;
  for (let j = 0; j < P.ent.length; j++) M[j] = draw.get(P.ent[j]).m;
  let c = 0;
  for (let i = 0; i < P.n; i++) {
    const b = 9 * i;
    A[b] = M[C[c++]];
    for (let s = 1; s < 9; s++) {
      const h = C[c++];
      if (h < 0) { A[b + s] = A[-1 - h]; continue; }
      let v = M[C[c++]];
      for (let t = 1; t < h; t++) v = Math.min(v, M[C[c++]]);
      A[b + s] = v;
    }
  }
  return P.slots;
}
